import React from 'react';
import { ArrowLeft, Calendar, MapPin, TrendingUp, Plane } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useLanguage } from '@/contexts/LanguageContext';

const SFExpressUKArticle = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50">
      <div className="container mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <Button 
            variant="ghost" 
            onClick={() => window.history.back()}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Industry Highlights
          </Button>
          
          <div className="flex items-center gap-4 mb-4">
            <Badge className="bg-blue-500">
              <TrendingUp className="h-3 w-3 mr-1" />
              {t('article.sfexpress.badge')}
            </Badge>
            <div className="flex items-center text-gray-600">
              <Calendar className="h-4 w-4 mr-2" />
              {t('article.sfexpress.date')}
            </div>
            <div className="flex items-center text-gray-600">
              <MapPin className="h-4 w-4 mr-2" />
              {t('article.sfexpress.location')}
            </div>
          </div>
          
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {t('article.sfexpress.title')}
          </h1>
          
          <p className="text-xl text-gray-600 leading-relaxed">
            {t('article.sfexpress.summary')}
          </p>
        </div>

        {/* Featured Image */}
        <div className="mb-8">
          <img 
            src="https://images.unsplash.com/photo-1436491865332-7a61a109cc05?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2074&q=80" 
            alt={t('article.sfexpress.imageCaption')}
            className="w-full h-96 object-cover rounded-lg shadow-lg"
          />
          <p className="text-sm text-gray-500 mt-2 text-center">
            {t('article.sfexpress.imageCaption')}
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Highlight Box */}
            <Card className="border-l-4 border-l-blue-500 bg-blue-50">
              <CardContent className="pt-6">
                <div className="flex items-start">
                  <Plane className="h-6 w-6 text-blue-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-blue-800 mb-2">Key Highlight</h3>
                    <p className="text-blue-700">{t('article.sfexpress.highlight')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Article Sections */}
            <div className="prose prose-lg max-w-none">
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  {t('article.sfexpress.section1.title')}
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  {t('article.sfexpress.section1.content')}
                </p>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  {t('article.sfexpress.section2.title')}
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  {t('article.sfexpress.section2.content')}
                </p>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  {t('article.sfexpress.section3.title')}
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  {t('article.sfexpress.section3.content')}
                </p>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  {t('article.sfexpress.section4.title')}
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  {t('article.sfexpress.section4.content')}
                </p>
              </div>
            </div>

            {/* Impact Analysis */}
            <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
              <CardHeader>
                <CardTitle className="flex items-center text-blue-800">
                  <TrendingUp className="h-5 w-5 mr-2" />
                  {t('article.sfexpress.impact.title')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-blue-700">
                  {t('article.sfexpress.impact.content')}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Key Metrics */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Key Metrics</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Provider</span>
                  <span className="font-semibold">SF Express</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Route</span>
                  <span className="font-semibold text-blue-600">China-UK</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Hub</span>
                  <span className="font-semibold">East Midlands</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Status</span>
                  <Badge className="bg-blue-500">New Routes</Badge>
                </div>
              </CardContent>
            </Card>

            {/* Source */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Source</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-4">
                  Travel and Tour World - Aviation News
                </p>
                <Button 
                  className="w-full" 
                  onClick={() => window.open('https://www.travelandtourworld.com/news/article/sf-express-launches-first-uk-flights-boosting-east-midlands-airports-cargo-hub-status/', '_blank')}
                >
                  Read Original Article
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SFExpressUKArticle;